import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID, DestroyRef } from '@angular/core';
import { Observable, Subject, fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface PageVisibilityHandler {
  onPageVisible(): void;
  onPageHidden?(): void;
}

// This event bus is used to listen for browser events (postMessage, tab focus, etc.)
@Injectable({
  providedIn: 'root',
})
export class BrowserEventBusService {
  private browserMessageSubject = new Subject<MessageEvent>();
  private visibilityHandlers: PageVisibilityHandler[] = [];

  private targetOrigin = '*';
  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);

  public browserMessage$: Observable<MessageEvent> = this.browserMessageSubject.asObservable();

  constructor() {
    console.log('BrowserEventBusService constructor');
    if (isPlatformBrowser(this.platformId)) {
      console.log('Setting up browser event listeners');
      this.ngZone.runOutsideAngular(() => {
        this.processMessage();
        this.processAvailabilityMessage();
        this.setupVisibilityListeners();
      });
    } else {
      console.log('Not in browser, skipping event setup');
    }
  }

  registerVisibilityHandler(handler: PageVisibilityHandler): void {
    console.log('Registering visibility handler, total handlers:', this.visibilityHandlers.length + 1);
    this.visibilityHandlers.push(handler);
  }

  unregisterVisibilityHandler(handler: PageVisibilityHandler): void {
    const index = this.visibilityHandlers.indexOf(handler);
    if (index > -1) {
      this.visibilityHandlers.splice(index, 1);
    }
  }

  //TODO: Fix event.origin check.
  processMessage(): void {
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        tap((event: MessageEvent) => {
          console.log('New Message Event: ', event?.data ?? 'No data in event', event.origin);
        }),
        //Filter out events that are not included in allowed origins or do not have a specific event type associated to them.
        filter(
          event =>
            //environment.ALLOWED_MESSAGE_ORIGINS.split(',').includes(event.origin) &&
            environment.ALLOWED_MESSAGE_TYPES.split(',').includes(event.data?.type) &&
            event.data?.data?.Token,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        this.ngZone.run(() => {
          this.targetOrigin = event.origin;
          console.log('Received browser post message in new pipeline: ', event);
          this.browserMessageSubject.next(event);
        });
      });
  }

  //TODO: Fix event.origin check.
  // TODO: May need to move these to an endpoint in the future instead of using window.postMessage.
  /**
   * Handles SET_SMS_AVAILABILITY postMessage events from parent windows (e.g., Salesforce).
   * Allows consuming applications to control user availability state.
   * Message format: { type: 'SET_SMS_AVAILABILITY', data: { available: boolean } }
   */
  processAvailabilityMessage(): void {
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(event => event.data?.type === 'SET_SMS_AVAILABILITY'),
        tap((event: MessageEvent) => {
          console.log('Availability message received:', event.data, event.origin);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        this.ngZone.run(() => {
          const available = event.data?.data?.available;
          if (typeof available === 'boolean') {
            if (available) {
              console.log('External availability set to true - notifying handlers');
              this.notifyPageVisible();
            } else {
              console.log('External availability set to false - notifying handlers');
              this.notifyPageHidden();
            }
          } else {
            console.warn('SET_SMS_AVAILABILITY message missing valid "available" value');
          }
        });
      });
  }

  sendMessage(message: Record<string, string>) {
    console.log('Sending browser post message out: ', message);
    if (isPlatformBrowser(this.platformId)) {
      try {
        if (this.isStandalone()) {
          window.postMessage(message, this.targetOrigin);
        } else {
          // Send to immediate parent window
          window.parent.postMessage(message, this.targetOrigin);
        }
      } catch (error) {
        console.error('Error sending postMessage:', error);
      }
    }
  }

  isStandalone() {
    try {
      return window.self === window.top;
    } catch {
      return true;
    }
  }

  private setupVisibilityListeners(): void {
    console.log('Setting up page visibility listeners');

    // Use Page Visibility API https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
    fromEvent(document, 'visibilitychange')
      .pipe(
        tap(() => console.log('Visibility change event:', document.visibilityState)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.ngZone.run(() => {
          if (document.visibilityState === 'visible') {
            console.log('Page became visible - notifying', this.visibilityHandlers.length, 'handlers');
            this.notifyPageVisible();
          } else if (document.visibilityState === 'hidden') {
            console.log('Page became hidden - notifying', this.visibilityHandlers.length, 'handlers');
            this.notifyPageHidden();
          }
        });
      });
  }

  private notifyPageVisible(): void {
    this.visibilityHandlers.forEach(handler => {
      try {
        handler.onPageVisible();
      } catch (error) {
        console.error('Error in page visible handler:', error);
      }
    });
  }

  private notifyPageHidden(): void {
    this.visibilityHandlers.forEach(handler => {
      try {
        handler.onPageHidden?.();
      } catch (error) {
        console.error('Error in page hidden handler:', error);
      }
    });
  }

}
